import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { getStoryAdmin, updateStory, generateStorySlug } from "@/lib/api/stories.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { SlugField } from "@/components/admin/SlugField";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { BlockDocEditor } from "@/components/admin/doc/BlockDocEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import type { ArticleBlock } from "@/lib/types/content";
import { MediaField } from "@/components/admin/MediaPicker";
import { coverFromInsight, ogFromInsight, type MediaSelection } from "@/lib/media/types";
import { resolveEditorDocForLoad } from "@/lib/doc/doc-convert";
import type { EditorDoc } from "@/lib/doc/doc-types";

export const Route = createFileRoute("/admin/stories/$id")({
  loader: ({ params }) => getStoryAdmin({ data: { id: params.id } }),
  component: EditStoryPage,
});

function EditStoryPage() {
  const story = Route.useLoaderData();
  if (!story) throw notFound();

  const [title, setTitle] = useState(story.title);
  const [slug, setSlug] = useState(story.slug);
  const [status, setStatus] = useState(story.status);
  const [client, setClient] = useState(story.client ?? "");
  const [industry, setIndustry] = useState(story.industry ?? "");
  const [service, setService] = useState(story.service ?? "");
  const [excerpt, setExcerpt] = useState(story.excerpt ?? "");
  const [editorDoc, setEditorDoc] = useState<EditorDoc>(() =>
    resolveEditorDocForLoad({
      editorDoc: story.editorDoc,
      craftState: story.craftState,
      contentBlocks: (story.contentBlocks ?? []) as ArticleBlock[],
      contentHtml: story.contentHtml,
    }),
  );
  const [outcomes, setOutcomes] = useState(
    ((story.outcomes ?? []) as { label: string; value: string }[]).length
      ? (story.outcomes as { label: string; value: string }[])
      : [{ label: "", value: "" }],
  );
  const [cover, setCover] = useState<MediaSelection>(coverFromInsight(story));
  const [ogImage, setOgImage] = useState<MediaSelection>(ogFromInsight(story));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateStory({
        data: {
          id: story.id,
          title,
          slug,
          status,
          client,
          industry,
          service,
          excerpt,
          editorDoc,
          outcomes: outcomes.filter((o) => o.label),
          coverMediaId: cover.mediaId,
          coverUrl: cover.url,
          ogImageMediaId: ogImage.mediaId,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Edit story" />
      <ContentAdminTabs
        settingsLabel="Story settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <SlugField title={title} slug={slug} onSlugChange={setSlug} onGenerate={() => generateStorySlug({ data: { title, excludeId: story.id } })} />
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2"><Label>Client</Label><Input value={client} onChange={(e) => setClient(e.target.value)} /></div>
              <div className="space-y-2"><Label>Industry</Label><Input value={industry} onChange={(e) => setIndustry(e.target.value)} /></div>
              <div className="space-y-2"><Label>Service</Label><Input value={service} onChange={(e) => setService(e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Excerpt</Label><Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Outcomes (KPI strip)</Label>
              {outcomes.map((o, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-2">
                  <Input
                    placeholder="Label"
                    value={o.label}
                    onChange={(e) => {
                      const next = [...outcomes];
                      next[i] = { ...next[i], label: e.target.value };
                      setOutcomes(next);
                    }}
                  />
                  <Input
                    placeholder="Value"
                    value={o.value}
                    onChange={(e) => {
                      const next = [...outcomes];
                      next[i] = { ...next[i], value: e.target.value };
                      setOutcomes(next);
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setOutcomes([...outcomes, { label: "", value: "" }])}
              >
                + Add outcome
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <MediaField label="Cover image" value={cover} onChange={setCover} />
              <MediaField label="OG image" value={ogImage} onChange={setOgImage} />
            </div>
          </div>
        }
        content={
          <BlockDocEditor
            editorKey={story.id}
            initialDoc={editorDoc}
            onChange={setEditorDoc}
            className="w-full"
          />
        }
      />
      <AdminFormFooter cancelTo="/admin/stories" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
